import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import placeDetailsData from '../../JsonData/placeDetails.json';

const PlaceDetailsPage = ({ placeId }) => {
  const params = useParams();
  const id = placeId || params.id;
  const [place, setPlace] = useState(null);

  useEffect(() => {
    // scroll to top and set place
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const found = placeDetailsData.find(p => p.id === parseInt(id));
    setPlace(found);
  }, [id]);

  if (!place) {
    return (
      <div className="h-screen flex items-center justify-center font-lora text-2xl">
        Loading or Place not found...
      </div>
    );
  }

  const handleBookNow = (room) => {
    const text = `Hello Znorm Homes! I am interested in booking the "${room.type}" at ${place.name}. Price given: ${room.price}. Please provide availability and next steps.`;
    const encodedText = encodeURIComponent(text);
    // You can replace the below dummy phone number with the exact registered WhatsApp number
    window.open(`https://wa.me/919999999999?text=${encodedText}`, '_blank');
  };

  const getIconClass = (iconName) => {
    switch (iconName) {
      case 'wifi': return 'fa-solid fa-wifi';
      case 'shield': return 'fa-solid fa-shield-halved';
      case 'laptop': return 'fa-solid fa-laptop';
      case 'dumbbell': return 'fa-solid fa-dumbbell';
      case 'pool': return 'fa-solid fa-water-ladder';
      case 'restaurant': return 'fa-solid fa-utensils';
      case 'spa': return 'fa-solid fa-spa';
      case 'view': return 'fa-solid fa-water';
      case 'food': return 'fa-solid fa-kitchen-set';
      case 'bus': return 'fa-solid fa-bus';
      case 'beach': return 'fa-solid fa-umbrella-beach';
      case 'yoga': return 'fa-solid fa-person-praying';
      case 'coffee': return 'fa-solid fa-mug-hot';
      case 'surf': return 'fa-solid fa-water';
      default: return 'fa-solid fa-check';
    }
  };

  const getReachIcon = (mode) => {
    if (mode.toLowerCase().includes('flight')) return 'fa-solid fa-plane';
    if (mode.toLowerCase().includes('train')) return 'fa-solid fa-train';
    if (mode.toLowerCase().includes('bus')) return 'fa-solid fa-bus';
    return 'fa-solid fa-location-dot';
  }

  const slideUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <main className="bg-[#FAFAFA] min-h-screen">
      {/* Hero Section */}
      <section className="relative w-full h-[50vh] md:h-[70vh] lg:h-[80vh]">
        <img 
          src={place.heroImage} 
          alt={place.name} 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <motion.h1 
             initial={{ opacity: 0, scale: 0.9 }} 
             animate={{ opacity: 1, scale: 1 }} 
             transition={{ duration: 0.8 }}
             className="text-white font-lora font-medium text-4xl md:text-6xl lg:text-[72px] text-center px-4 tracking-wide"
          >
            {place.name}
          </motion.h1>
        </div>
      </section>

      <div className="max-w-[1300px] mx-auto px-4 md:px-8 py-16 md:py-24 space-y-24">
        
        {/* Know More */}
        <motion.section {...slideUp} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-lora font-medium text-3xl md:text-4xl lg:text-[44px] text-[#151515] mb-6">
              Know the Place
            </h2>
            <p className="font-outfit text-gray-700 leading-relaxed md:text-lg text-justify">
              {place.about}
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl relative aspect-[4/3] lg:aspect-auto lg:h-[500px]">
             <img 
               src={place.heroImage} 
               alt="About Place" 
               className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" 
             />
          </div>
        </motion.section>

        {/* Amenities */}
        <motion.section {...slideUp} className="bg-[#9ABB7A]/5 rounded-[30px] p-8 md:p-16 border border-[#9ABB7A]/20">
          <h2 className="font-lora font-medium text-3xl md:text-4xl text-center text-[#151515] mb-12">
            Premium Amenities
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 text-center">
            {place.amenities.map((amenity, idx) => (
               <div key={idx} className="flex flex-col items-center justify-center group space-y-4">
                  <div className="w-20 h-20 bg-white rounded-full shadow-sm border border-gray-100 flex items-center justify-center text-[#9ABB7A] text-3xl group-hover:bg-[#9ABB7A] group-hover:text-white transition-colors duration-300">
                    <i className={getIconClass(amenity.icon)}></i>
                  </div>
                  <h3 className="font-outfit text-lg font-medium text-[#151515]">
                    {amenity.name}
                  </h3>
               </div>
            ))}
          </div>
        </motion.section>

        {/* Rooms & Booking */}
        <motion.section {...slideUp}>
          <h2 className="font-lora font-medium text-3xl md:text-4xl text-center text-[#151515] mb-12">
            Select Your Space
          </h2>
          <div className="space-y-12">
             {place.rooms.map((room, index) => (
               <div 
                 key={room.id} 
                 className={`bg-white rounded-[24px] shadow-lg overflow-hidden flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}
               >
                 <div className="lg:w-1/2 flex">
                    <img 
                      src={room.images[0]} 
                      alt={room.type} 
                      className="w-full h-64 lg:h-full object-cover hover:opacity-90 transition-opacity duration-300" 
                    />
                 </div>
                 <div className="lg:w-1/2 p-8 md:p-12 flex flex-col justify-center space-y-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <h3 className="font-lora font-medium text-3xl text-[#151515]">{room.type}</h3>
                      <span className="font-outfit font-medium text-lg bg-[#9ABB7A] text-white px-5 py-2 rounded-full whitespace-nowrap shadow-sm">
                        {room.price}
                      </span>
                    </div>
                    <p className="font-outfit text-gray-600 leading-relaxed text-lg">
                      {room.description}
                    </p>
                    
                    <div className="py-2">
                       <h4 className="font-outfit font-medium text-[#151515] mb-3">Room Gallery</h4>
                       <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                         {room.images.map((img, i) => (
                             <img 
                               key={i} 
                               src={img} 
                               alt="Gallery Thumb" 
                               className="w-24 h-24 rounded-xl object-cover shadow-sm border border-gray-100 hover:scale-105 transition-transform" 
                             />
                         ))}
                       </div>
                    </div>

                    <button 
                      onClick={() => handleBookNow(room)}
                      className="mt-6 flex items-center justify-center gap-3 w-full md:w-auto self-start bg-black text-white px-8 py-4 rounded-full font-outfit text-lg hover:bg-[#25D366] transition-colors duration-300 shadow-md"
                    >
                      <span>Book Now via WhatsApp</span>
                      <i className="fa-brands fa-whatsapp text-2xl"></i>
                    </button>
                 </div>
               </div>
             ))}
          </div>
        </motion.section>

        {/* Ways to Reach */}
        <motion.section {...slideUp} className="bg-white rounded-[30px] border border-gray-100 p-8 md:p-16 mb-12 shadow-sm">
          <h2 className="font-lora font-medium text-3xl md:text-4xl text-center text-[#151515] mb-12">
            How to Reach
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {place.reach.map((route, i) => (
              <div key={i} className="p-8 rounded-2xl hover:bg-[#FAFAFA] transition-colors duration-300 border border-transparent hover:border-gray-100">
                 <div className="w-16 h-16 mx-auto bg-[#9ABB7A]/10 rounded-full flex items-center justify-center text-[#9ABB7A] text-2xl mb-6">
                   <i className={getReachIcon(route.mode)}></i>
                 </div>
                 <h3 className="font-lora font-medium text-2xl text-[#151515] mb-4">{route.mode}</h3>
                 <p className="font-outfit text-gray-600 leading-relaxed text-[15px]">{route.details}</p>
              </div>
            ))}
          </div>
        </motion.section>

      </div>
    </main>
  );
};

export default PlaceDetailsPage;
